// #region IMPORTS
import { AZModal, setFeedBackOpen } from '@azmed-org/azcomponents';
import { MdArrowBack, MdHelpOutline, MdSettings } from 'react-icons/md';
import { asyncComponent, retryImport } from '@ohif/ui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VscFeedback } from 'react-icons/vsc';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { UserPreferences } from '../../components/UserPreferences';
import { FeedbackWrapper } from '../AZFeedback/FeedbackWrapper';
import { AuthButton } from './AuthButton';
import { Title } from '../Components/Title/Title';
import { selectVertical } from '../Components/Filter/FiltersSlice.js';
import { setKeepFiltersOnReset, triggerResetApp } from '../NavigationSlice.js';
import { IS_OFFLINE_BUILD } from '../Config/buildMode';
import { OfflineImportTab } from '../OfflineImport/OfflineImportView/OfflineImportTab';
import { OfflineDownloadPanel } from '../OfflineDownload/OfflineDownloadPanel';
import { isAzmedUser } from '../OfflineDownload/offlineDownloadUtils';
import { useAppAuth } from '../Auth/authAdapter';
import { getReleaseVersion } from '../Utils/appVersion';
// CSS
import './AZmedNavbar.css';
// #endregion

// #region CONSTANTS
const HELP_LINK =
  'https://www.notion.so/azmed/How-to-use-AZViewer-50f54c9e32154276a7f1c6902d551be2?pvs=4';
// #endregion

// #region STYLES
// AZModal already provides the panel background; these inner containers must
// not paint their own, otherwise the modal shows a nested darker box.
const PREFERENCES_CONTENT = {
  width: '100%',
  height: '100%',
  color: 'white',
};
// #endregion

// #region ASYNC COMPONENTS
// Local files loader, fetched in its own chunk on first open
const ViewerLocalFileData = asyncComponent(() =>
  retryImport(() =>
    import(
      /* webpackChunkName: "ViewerLocalFileData" */ '../../connectedComponents/LoadLocalFiles.js'
    )
  )
);
// #endregion

// #region COMPONENT
/**
 * Return the navbar of the App
 * @returns {JSX.Element} The navbar and its modals
 */
function AzmedNavbar() {
  // #region INITIALIZATION
  // State
  const [openDicomModale, setOpenDicomModale] = useState(false);

  // Selectors
  const vertical = useSelector(selectVertical);

  // Hooks
  const history = useHistory();
  const location = useLocation();
  const [t] = useTranslation('Navbar');
  const { user } = useAppAuth();

  // Dispatch
  const dispatch = useDispatch();

  // Offline build status pill and release version shown next to the title
  const releaseVersion = getReleaseVersion();
  const titleStatus = IS_OFFLINE_BUILD ? t('Offline') : null;
  const titleVersion =
    IS_OFFLINE_BUILD && releaseVersion ? `v${releaseVersion}` : null;
  // #endregion

  // #region EVENT
  // Close Open dicom modale
  const handleClose = () => {
    setOpenDicomModale(false);
  };

  // Return to the study list
  const handleGoBack = () => {
    history.replace('/');
  };

  // Open the feedback modal
  const handleOpenFeedback = () => {
    dispatch(setFeedBackOpen('manually'));
  };

  // Reset filters and go to home
  const handleClick = () => {
    dispatch(triggerResetApp());
    history.replace('/');
  };

  // Reset app but keep filters and go to home
  const handleResetAppAndKeepFilters = () => {
    dispatch(setKeepFiltersOnReset(true));
  };
  // #endregion

  // #region UTILS
  // Whether the current route displays the navbar toolbar
  const doShowToolbar = () => {
    return (
      location.pathname === '/' ||
      location.pathname.startsWith('/viewer') ||
      location.pathname.startsWith('/local')
    );
  };
  // #endregion

  // #region INTERFACE
  // Tool tabs appended to the settings ones, always 3 for stable indexes
  const toolTabs = [
    {
      name: 'Local files',
      Component: ViewerLocalFileData,
      customProps: { onLoaded: handleClose },
      hidden: IS_OFFLINE_BUILD,
    },
    {
      name: 'Offline mode',
      Component: OfflineDownloadPanel,
      customProps: {},
      hidden: IS_OFFLINE_BUILD || !isAzmedUser(user?.email),
    },
    {
      name: 'Import data',
      Component: OfflineImportTab,
      customProps: {},
      hidden: !IS_OFFLINE_BUILD,
    },
  ];

  return (
    <>
      <div className="AZmedNavbar-root">
        <nav className="AZmedNavbar-appBar">
          <div className="AZmedNavbar-link" onClick={handleClick}>
            <Title
              label="Viewer"
              showImage={true}
              showLines={false}
              capitalize={false}
              status={titleStatus}
              version={titleVersion}
            />
          </div>
          {doShowToolbar() && (
            <>
              <div className="AZmedNavbar-grow" />
              {location.pathname !== '/' ? (
                <button
                  aria-haspopup="true"
                  className="AZmedNavbar-navButton"
                  onClick={handleGoBack}
                >
                  <MdArrowBack className="AZmedNavbar-Icon" />
                  <div className="AZmedNavbar-sectionDesktop">
                    {t('Study list')}
                  </div>
                </button>
              ) : (
                vertical && (
                  <button
                    aria-haspopup="true"
                    className="AZmedNavbar-navButton"
                    onClick={handleResetAppAndKeepFilters}
                  >
                    <MdArrowBack className="AZmedNavbar-Icon" />
                    <div className="AZmedNavbar-sectionDesktop">
                      {t('Home page')}
                    </div>
                  </button>
                )
              )}
              <button
                aria-haspopup="true"
                className="AZmedNavbar-navButton"
                onClick={() => setOpenDicomModale(true)}
              >
                <MdSettings className="AZmedNavbar-Icon" />
                <div className="AZmedNavbar-sectionDesktop">
                  {t('Settings')}
                </div>
              </button>
              {/* Help (external link), feedback and account: online only */}
              {!IS_OFFLINE_BUILD && (
                <>
                  <button
                    aria-haspopup="true"
                    className="AZmedNavbar-navButton"
                    onClick={() => window.open(HELP_LINK, '_blank')}
                  >
                    <MdHelpOutline className="AZmedNavbar-Icon" />
                    <div className="AZmedNavbar-sectionDesktop">
                      {t('Help')}
                    </div>
                  </button>
                  {/* Feedbacks */}
                  <button
                    aria-haspopup="true"
                    className="AZmedNavbar-navButton"
                    onClick={() => handleOpenFeedback()}
                  >
                    <VscFeedback
                      className="AZmedNavbar-Icon"
                      fontWeight={'bold'}
                    />
                    <div className="AZmedNavbar-sectionDesktop">
                      {t('Feedback')}
                    </div>
                  </button>
                  <AuthButton />
                </>
              )}
            </>
          )}
        </nav>
      </div>
      <AZModal
        show={openDicomModale}
        onClose={handleClose}
        fullscreen={false}
        maxWidth="900px"
      >
        <div className="AZmedNavbar-ModalContainer" style={PREFERENCES_CONTENT}>
          <UserPreferences hide={handleClose} extraTabs={toolTabs} />
        </div>
      </AZModal>
      {!IS_OFFLINE_BUILD && <FeedbackWrapper />}
    </>
  );
  // #endregion
}
// #endregion

export default AzmedNavbar;
